// Tiny fetch wrapper. The BFF reads the JWT from the httpOnly fdn_token
// cookie OR Authorization header. The cookie path is the production
// browser flow; the Authorization header path is used by the
// localStorage-based account switcher (utils/accounts.ts) which lets
// developers test multi-user flows without constant logout/login.
//
// When an active account is saved, every request gains
//   Authorization: Bearer <token>
// in addition to the cookie. The BFF's extractToken (server/src/auth.ts)
// reads Authorization first, so the Bearer wins when both are present.
import { getActiveToken } from './accounts.js';
const API_BASE = '/api';
/**
 * Build headers including the Bearer token from the active account
 * when one is set. Cookie auth still works when no token is in
 * localStorage; this just layers on top.
 */
function authHeaders(extra) {
    const headers = { ...(extra ?? {}) };
    const token = getActiveToken();
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }
    return headers;
}
export class ApiError extends Error {
    status;
    body;
    constructor(message, status, body) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.body = body;
    }
}
async function request(method, path, body, headers) {
    const init = {
        method,
        credentials: 'include',
        headers: authHeaders(headers),
    };
    if (body !== undefined) {
        init.headers['Content-Type'] = 'application/json';
        init.body = JSON.stringify(body);
    }
    const r = await fetch(`${API_BASE}${path}`, init);
    let parsed;
    const text = await r.text();
    try {
        parsed = text ? JSON.parse(text) : null;
    }
    catch {
        parsed = text;
    }
    if (!r.ok) {
        const msg = typeof parsed === 'object' && parsed && 'error' in parsed
            ? String(parsed.error)
            : `${method} ${path} -> ${r.status}`;
        throw new ApiError(msg, r.status, parsed);
    }
    return parsed;
}
export const api = {
    get: (path) => request('GET', path),
    post: (path, body) => request('POST', path, body),
    patch: (path, body) => request('PATCH', path, body),
    put: (path, body) => request('PUT', path, body),
    delete: (path, body) => request('DELETE', path, body),
    /** Multipart upload helper -- pass FormData directly. */
    upload: async (path, formData) => {
        const r = await fetch(`${API_BASE}${path}`, {
            method: 'POST',
            credentials: 'include',
            headers: authHeaders(),
            body: formData,
        });
        let parsed;
        const text = await r.text();
        try {
            parsed = text ? JSON.parse(text) : null;
        }
        catch {
            parsed = text;
        }
        if (!r.ok) {
            const msg = typeof parsed === 'object' && parsed && 'error' in parsed
                ? String(parsed.error)
                : `upload -> ${r.status}`;
            throw new ApiError(msg, r.status, parsed);
        }
        return parsed;
    },
};
/**
 * Open an SSE stream to the given API path and dispatch events. Returns a
 * function the caller can call to abort.
 */
export function openSSE(path, onEvent, onError) {
    const ac = new AbortController();
    (async () => {
        try {
            const r = await fetch(`${API_BASE}${path}`, {
                method: 'GET',
                credentials: 'include',
                headers: authHeaders({ Accept: 'text/event-stream' }),
                signal: ac.signal,
            });
            if (!r.ok || !r.body) {
                const txt = await r.text().catch(() => '');
                throw new Error(`SSE ${path} -> ${r.status} ${txt}`);
            }
            const reader = r.body.getReader();
            const decoder = new TextDecoder();
            let buf = '';
            while (true) {
                const { value, done } = await reader.read();
                if (done)
                    break;
                buf += decoder.decode(value, { stream: true });
                let idx;
                while ((idx = buf.indexOf('\n\n')) !== -1) {
                    const frame = buf.slice(0, idx);
                    buf = buf.slice(idx + 2);
                    let event = 'message';
                    let data = '';
                    for (const line of frame.split('\n')) {
                        if (line.startsWith('event:'))
                            event = line.slice(6).trim();
                        else if (line.startsWith('data:'))
                            data += line.slice(5).trim();
                    }
                    if (data) {
                        try {
                            onEvent(event, JSON.parse(data));
                        }
                        catch {
                            onEvent(event, data);
                        }
                    }
                }
            }
        }
        catch (err) {
            if (err.name === 'AbortError')
                return;
            onError?.(err);
        }
    })();
    return () => ac.abort();
}
/**
 * POST to an SSE endpoint and dispatch events. Used for chat replies.
 */
export function streamPostSSE(path, body, onEvent, onError) {
    const ac = new AbortController();
    (async () => {
        try {
            const r = await fetch(`${API_BASE}${path}`, {
                method: 'POST',
                credentials: 'include',
                headers: authHeaders({ 'Content-Type': 'application/json', Accept: 'text/event-stream' }),
                body: JSON.stringify(body),
                signal: ac.signal,
            });
            if (!r.ok || !r.body) {
                const txt = await r.text().catch(() => '');
                throw new Error(`SSE POST ${path} -> ${r.status} ${txt}`);
            }
            const reader = r.body.getReader();
            const decoder = new TextDecoder();
            let buf = '';
            while (true) {
                const { value, done } = await reader.read();
                if (done)
                    break;
                buf += decoder.decode(value, { stream: true });
                let idx;
                while ((idx = buf.indexOf('\n\n')) !== -1) {
                    const frame = buf.slice(0, idx);
                    buf = buf.slice(idx + 2);
                    let event = 'message';
                    let data = '';
                    for (const line of frame.split('\n')) {
                        if (line.startsWith('event:'))
                            event = line.slice(6).trim();
                        else if (line.startsWith('data:'))
                            data += line.slice(5).trim();
                    }
                    if (data) {
                        try {
                            onEvent(event, JSON.parse(data));
                        }
                        catch {
                            onEvent(event, data);
                        }
                    }
                }
            }
        }
        catch (err) {
            if (err.name === 'AbortError')
                return;
            onError?.(err);
        }
    })();
    return () => ac.abort();
}
